import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from './layout/Layout';
import axios from 'axios';

// Declare global types if needed
declare global {
  interface Window {
    $: any;
    jQuery: any;
    bootstrap: any;
  }
}

interface User {
  id: number;
  username: string;
  email: string;
  account_number: string;
  phone_number: string;
  name: string;
  age: number;
  location: string;
  profile_picture: string | null;
  is_verified: boolean;
  is_banned: boolean;
  banned_on?: string;
  banned_duration?: string;
  ban_expires_at?: string;
  created_at: string;
  last_login: string;
  gallery_count?: number;
  library_count?: number;
  conversation_count?: number;
  reports_count?: number;
  is_live?: boolean;
  otp?: string | null;
  otp_expired_at?: string | null;
  otp_attempts?: number;
  is_deleted?: boolean;
  deleted_at?: string | null;
  password?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  location_name?: string | null;
}

interface UserStats {
  total: number;
  active: number;
  verified: number;
  today: number;
  banned: number;
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalRecords: number;
  recordsPerPage: number;
  startRecord: number;
  endRecord: number;
}

interface SortInfo {
  column: string;
  direction: 'asc' | 'desc';
}

const Users: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<UserStats>({ total: 0, active: 0, verified: 0, today: 0, banned: 0 });
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationInfo>({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    recordsPerPage: 10,
    startRecord: 1,
    endRecord: 10
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortInfo, setSortInfo] = useState<SortInfo>({
    column: 'id',
    direction: 'asc'
  });
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [showBanModal, setShowBanModal] = useState(false);
  const [banDuration, setBanDuration] = useState('');
  const [showUserDetailsModal, setShowUserDetailsModal] = useState(false);
  const [banLoading, setBanLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const [showReportsModal, setShowReportsModal] = useState(false);
  const [userReports, setUserReports] = useState<any[]>([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [galleryData, setGalleryData] = useState<any[]>([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [libraryData, setLibraryData] = useState<any[]>([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<any>(null);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showSetLocationModal, setShowSetLocationModal] = useState(false);
  const [setLocationLoading, setSetLocationLoading] = useState(false);
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [locationName, setLocationName] = useState('');
  const tableRef = useRef<HTMLTableElement>(null);
  const initialDataFetched = useRef(false);

  // User selection state for bulk email
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);

  // Filter states
  const [filters, setFilters] = useState({
    verification: 'all', // all, verified, not_verified
    restriction: 'all', // all, banned, not_banned
    maxAge: '', // only max age, min is fixed at 1
    relationshipStatus: [], // multiselect from relationship_status table
    online: 'all', // all, yes, no
    positions: [], // multiselect from positions table
    bodyTypes: [], // multiselect from body_types table
    tags: [], // multiselect from tags table (via tag_categories)
    deleted: 'active', // active, deleted, all
    hasAlbum: 'all', // all, yes, no
    hasPicture: 'all', // all, yes, no (gallery)
    hasProfilePicture: 'all', // all, yes, no
  });

  // Filter options states
  const [filterOptions, setFilterOptions] = useState({
    relationshipStatuses: [],
    positions: [],
    bodyTypes: [],
    tagCategories: [],
    tags: []
  });

  // Fetch filter options
  const fetchFilterOptions = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get('/admin/filter-options', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setFilterOptions(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  // Refresh user statistics (force refresh)
  const refreshStats = async () => {
    try {
      const response = await axios.get('/admin/users/stats');
      if (response.data.success) {
        setStats(response.data.data);
        console.log('✅ Stats refreshed successfully');
      }
    } catch (error) {
      console.error('Error refreshing user stats:', error);
      if (error instanceof Error) {
        console.error('Stats Refresh Error Stack:', error.stack);
      }
    }
  };

  // Fetch users data with pagination and search
  const fetchUsers = async (page: number = 1, search: string = '', sortColumn: string = 'id', sortDirection: string = 'asc', limit: number = recordsPerPage) => {
    return fetchUsersWithFilters(page, search, sortColumn, sortDirection, limit, filters);
  };

  const fetchUsersWithFilters = async (page: number = 1, search: string = '', sortColumn: string = 'id', sortDirection: string = 'asc', limit: number = recordsPerPage, filtersToUse: any = filters) => {
    try {
      setTableLoading(true);
      console.log('🔄 Fetching users data for page:', page, 'search:', search, 'sort:', sortColumn, sortDirection, 'limit:', limit, 'filters:', filtersToUse);
      const token = localStorage.getItem('admin_token');
      const start = (page - 1) * limit;
      
      const response = await axios.post('/admin/users', {
        draw: page,
        start: start,
        length: limit,
        search: {
          value: search
        },
        order: [{
          column: sortColumn,
          dir: sortDirection
        }],
        filters: filtersToUse // Use the provided filters
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('✅ Users fetched successfully:', response.data);
      console.log('🔍 Response data structure:', {
        hasData: !!response.data.data,
        isArray: Array.isArray(response.data.data),
        dataLength: response.data.data?.length,
        recordsTotal: response.data.recordsTotal,
        recordsFiltered: response.data.recordsFiltered
      });
      
              if (response.data.data && Array.isArray(response.data.data)) {
                setUsers(response.data.data);
        
                // Update pagination info
                // Use recordsFiltered when filters are applied, otherwise use recordsTotal
                const hasActiveFilters = Boolean(searchTerm) || hasActiveFilterValues(filtersToUse);
                const totalRecords = hasActiveFilters ? (response.data.recordsFiltered || 0) : (response.data.recordsTotal || 0);
                const totalPages = Math.ceil(totalRecords / limit);
                const startRecord = start + 1;
                const endRecord = Math.min(start + limit, totalRecords);
                
                console.log('📊 Pagination calculation:', {
                  hasActiveFilters,
                  recordsTotal: response.data.recordsTotal,
                  recordsFiltered: response.data.recordsFiltered,
                  usingTotalRecords: totalRecords,
                  totalPages,
                  currentPage: page
                });
        
        setPagination(prev => ({
          ...prev,
          currentPage: page,
          totalPages: totalPages,
          totalRecords: totalRecords,
          recordsPerPage: limit,
          startRecord: startRecord,
          endRecord: endRecord
        }));
        
                console.log('📊 Users set in state:', response.data.data.length);
                console.log('📄 Pagination updated:', { page, totalPages, totalRecords });
              } else {
                console.log('⚠️ No users data in response');
                setUsers([]);
              }
            } catch (error) {
              console.error('❌ Error fetching users:', error);
              if (error instanceof Error) {
                console.error('Users Error Stack:', error.stack);
              }
              setUsers([]);
            } finally {
              setTableLoading(false);
            }
          };

  // Initialize simple table (no DataTables for now)
  const initializeSimpleTable = () => {
    console.log('🔄 Initializing simple table...');
    // Data is already fetched in the first useEffect, no need to fetch again
    console.log('📊 Table initialized with existing data');
  };

  // Fetch user reports
  const fetchUserReports = async (userId: number) => {
    setReportsLoading(true);
    try {
      const response = await axios.get(`/admin/users/${userId}/reports`);
      if (response.data.success) {
        setUserReports(response.data.data.reports);
        console.log('User reports:', response.data.data.reports);
      }
    } catch (error) {
      console.error('Error fetching user reports:', error);
    } finally {
      setReportsLoading(false);
    }
  };

  // Fetch user gallery
  const fetchUserGallery = async (userId: number) => {
    setGalleryLoading(true);
    try {
      const response = await axios.get(`/admin/users/${userId}/gallery`);
      if (response.data.success) {
        setGalleryData(response.data.data.gallery);
        console.log('User gallery:', response.data.data.gallery);
      }
    } catch (error) {
      console.error('Error fetching user gallery:', error);
    } finally {
      setGalleryLoading(false);
    }
  };

  // Fetch user library
  const fetchUserLibrary = async (userId: number) => {
    setLibraryLoading(true);
    try {
      const response = await axios.get(`/admin/users/${userId}/libraries`);
      if (response.data.success) {
        setLibraryData(response.data.data.libraries);
        console.log('User library:', response.data.data.libraries);
      }
    } catch (error) {
      console.error('Error fetching user library:', error);
    } finally {
      setLibraryLoading(false);
    }
  };

  // Fetch reports when reports modal opens
  useEffect(() => {
    if (showReportsModal && selectedUser) {
      fetchUserReports(selectedUser.id);
    }
  }, [showReportsModal, selectedUser]);

  // Fetch gallery when gallery modal opens
  useEffect(() => {
    if (showGalleryModal && selectedUser) {
      fetchUserGallery(selectedUser.id);
    }
  }, [showGalleryModal, selectedUser]);


  // Fetch library when library modal opens
  useEffect(() => {
    if (showLibraryModal && selectedUser) {
      fetchUserLibrary(selectedUser.id);
    }
  }, [showLibraryModal, selectedUser]);


  // Open lightbox for media
  const openLightbox = (media: any) => {
    setSelectedMedia(media);
    setShowLightbox(true);
  };

  // Close lightbox
  const closeLightbox = () => {
    setShowLightbox(false);
    setSelectedMedia(null);
  };

  // View user details
  const viewUser = async (userId: number) => {
    try {
      const response = await axios.get(`/admin/users/${userId}`);
      if (response.data.success) {
        setSelectedUser(response.data.data.user);
        setShowUserDetailsModal(true);
        console.log('User details:', response.data.data);
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
      if (error instanceof Error) {
        console.error('User Details Error Stack:', error.stack);
      }
    }
  };

  // Ban user
  const banUser = (userId: number) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setSelectedUser(user);
      setShowBanModal(true);
    }
  };

  // User selection handlers
  const handleSelectUser = (userId: number) => {
    setSelectedUserIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUserIds.length === users.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(users.map(user => user.id));
    }
  };

  // Send email handlers
  const handleSendEmailToSelected = () => {
    if (selectedUserIds.length === 0) return;
    navigate('/communication/send-email', { 
      state: { userIds: selectedUserIds } 
    });
  };

  const handleSendEmailToUser = (userId: number) => {
    navigate('/communication/send-email', { 
      state: { userIds: [userId] } 
    });
  };

  // Send push handlers
  const handleSendPushToSelected = () => {
    if (selectedUserIds.length === 0) return;
    navigate('/communication/send-push', { 
      state: { userIds: selectedUserIds } 
    });
  };

  const handleSendPushToUser = (userId: number) => {
    navigate('/communication/send-push', { 
      state: { userIds: [userId] } 
    });
  };

  // Pagination functions
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setTableLoading(true);
      fetchUsers(page, searchTerm, sortInfo.column, sortInfo.direction, recordsPerPage);
    }
  };

  const handlePreviousPage = () => {
    if (pagination.currentPage > 1) {
      handlePageChange(pagination.currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (pagination.currentPage < pagination.totalPages) {
      handlePageChange(pagination.currentPage + 1);
    }
  };

  // Search functions
  const handleSearch = (value: string) => {
    setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset to first page
    setTableLoading(true);
    fetchUsers(1, value, sortInfo.column, sortInfo.direction, recordsPerPage);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      handleSearch(value);
    }, 300); // 300ms debounce
  };

  const handleSort = (column: string) => {
    let newDirection: 'asc' | 'desc' = 'asc';
    
    // If clicking the same column, toggle direction
    if (sortInfo.column === column) {
      newDirection = sortInfo.direction === 'asc' ? 'desc' : 'asc';
    }
    
    setSortInfo({ column, direction: newDirection });
    setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset to first page
    setTableLoading(true);
    fetchUsers(1, searchTerm, column, newDirection, recordsPerPage);
  };

  const handleRecordsPerPageChange = (newLimit: number) => {
    setRecordsPerPage(newLimit);
    setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset to first page
    setTableLoading(true);
    fetchUsers(1, searchTerm, sortInfo.column, sortInfo.direction, newLimit);
  };

  // Filter handlers
  const handleFilterChange = (filterType: string, value: any) => {
    const newFilters = {
      ...filters,
      [filterType]: value
    };
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset to first page
    setTableLoading(true);
    
    // Use the new filters immediately
    fetchUsersWithFilters(1, searchTerm, sortInfo.column, sortInfo.direction, recordsPerPage, newFilters);
  };

  const hasActiveFilterValues = (filtersObj: Record<string, any>) => {
    return Object.entries(filtersObj).some(([key, filter]) => {
      if (Array.isArray(filter)) {
        return filter.length > 0;
      }
      if (key === 'deleted') {
        return filter !== 'active';
      }
      return filter !== 'all' && filter !== '';
    });
  };

  const clearAllFilters = () => {
    const clearedFilters = {
      verification: 'all',
      restriction: 'all',
      maxAge: '',
      relationshipStatus: [],
      online: 'all',
      positions: [],
      bodyTypes: [],
      tags: [],
      deleted: 'active',
      hasAlbum: 'all',
      hasPicture: 'all',
      hasProfilePicture: 'all',
    };
    setFilters(clearedFilters);
    setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset to first page
    setTableLoading(true);
    fetchUsersWithFilters(1, searchTerm, sortInfo.column, sortInfo.direction, recordsPerPage, clearedFilters);
  };

  // Open Gallery Modal
  const openGalleryModal = (userId: number) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setSelectedUser(user);
      setShowGalleryModal(true);
    }
  };

  // Open Library Modal
  const openLibraryModal = (userId: number) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setSelectedUser(user);
      setShowLibraryModal(true);
    }
  };

  // Open Conversation Page
  const openConversationModal = (userId: number) => {
    navigate(`/chat/${userId}`);
  };

  // Open Reports Modal
  const openReportsModal = (userId: number) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setSelectedUser(user);
      setShowReportsModal(true);
    }
  };

  // Handle ban/unban submission
  const handleBanUser = async () => {
    if (!selectedUser) return;

    setBanLoading(true);
    
    try {
      let response;
      
      if (selectedUser.is_banned) {
        // Remove ban
        response = await axios.delete(`/admin/users/${selectedUser.id}/ban`);
      } else {
        // Ban user
        response = await axios.post('/admin/users/ban', {
          userId: selectedUser.id,
          banDuration: banDuration
        });
      }

      if (response.data.success) {
        setShowBanModal(false);
        setBanDuration('');
        setSelectedUser(null);
        
        // Show success message
        const action = selectedUser.is_banned ? 'removed' : 'applied';
        console.log(`✅ Ban ${action} successfully for user: ${selectedUser.name}`);
        
        // Refresh the data and stats
        setTableLoading(true);
        await Promise.all([
          fetchUsers(1, searchTerm, sortInfo.column, sortInfo.direction, recordsPerPage),
          refreshStats()
        ]);
      }
    } catch (error) {
      console.error('Error banning/unbanning user:', error);
      if (error instanceof Error) {
        console.error('Ban User Error Stack:', error.stack);
      }
    } finally {
      setBanLoading(false);
    }
  };

  // Handle delete user
  const handleDeleteUser = async (userId: number) => {
    const userToDelete = users.find(user => user.id === userId);
    const userName = userToDelete ? userToDelete.name : 'this user';
    
    if (!window.confirm(`Are you sure you want to delete ${userName}? This action cannot be undone.`)) {
      return;
    }

    setDeleteLoading(true);
    
    try {
      const response = await axios.delete(`/admin/users/${userId}`);
      
      if (response.data.success) {
        console.log('✅ User deleted successfully');
        
        // Refresh the data and stats
        setTableLoading(true);
        await Promise.all([
          fetchUsers(1, searchTerm, sortInfo.column, sortInfo.direction, recordsPerPage),
          refreshStats()
        ]);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      if (error instanceof Error) {
        console.error('Delete User Error Stack:', error.stack);
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  // Handle reset password
  const handleResetPassword = async () => {
    if (!selectedUser) return;

    // Validation
    if (!newPassword || !confirmPassword) {
      alert('Please fill in all fields');
      return;
    }

    if (newPassword.length < 6) {
      alert('Password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    setResetPasswordLoading(true);

    try {
      const response = await axios.patch(`/admin/users/${selectedUser.id}/reset-password`, {
        new_password: newPassword
      });

      if (response.data.success) {
        alert('Password reset successfully');
        setShowResetPasswordModal(false);
        setNewPassword('');
        setConfirmPassword('');
        setSelectedUser(null);
      } else {
        alert(response.data.message || 'Failed to reset password');
      }
    } catch (error: any) {
      console.error('Error resetting password:', error);
      const errorMessage = error.response?.data?.message || 'Failed to reset password';
      alert(errorMessage);
    } finally {
      setResetPasswordLoading(false);
    }
  };

  // Handle set location
  const handleSetLocation = async () => {
    if (!selectedUser) return;

    // Validation
    if (!latitude || !longitude) {
      alert('Please fill in latitude and longitude');
      return;
    }

    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);

    if (isNaN(lat) || isNaN(lng)) {
      alert('Latitude and longitude must be valid numbers');
      return;
    }

    if (lat < -90 || lat > 90) {
      alert('Latitude must be between -90 and 90');
      return;
    }

    if (lng < -180 || lng > 180) {
      alert('Longitude must be between -180 and 180');
      return;
    }

    setSetLocationLoading(true);

    try {
      const response = await axios.patch(`/admin/users/${selectedUser.id}/location`, {
        latitude: lat,
        longitude: lng,
        location_name: locationName || null
      });

      if (response.data.success) {
        alert('Location updated successfully');
        setShowSetLocationModal(false);
        setLatitude('');
        setLongitude('');
        setLocationName('');
        setSelectedUser(null);
      } else {
        alert(response.data.message || 'Failed to update location');
      }
    } catch (error: any) {
      console.error('Error updating location:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update location';
      alert(errorMessage);
    } finally {
      setSetLocationLoading(false);
    }
  };

  useEffect(() => {
    // Check if user is authenticated
    const token = localStorage.getItem('admin_token');
    if (!token) {
      console.error('No admin token found - user not authenticated');
      setLoading(false);
      return;
    }
    
    // Prevent multiple initial data fetches
    if (initialDataFetched.current) {
      console.log('Initial data already fetched, skipping...');
      setLoading(false);
      return;
    }
    
            console.log('User is authenticated, fetching stats, filter options and users...');
            initialDataFetched.current = true;
            refreshStats();
            fetchFilterOptions();
            fetchUsers(1, '', sortInfo.column, sortInfo.direction, recordsPerPage); // Load first page with default sorting
            setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!loading) {
      // Initialize simple table after component mounts
      setTimeout(() => {
        try {
          initializeSimpleTable();
        } catch (error) {
          console.error('Error in table initialization useEffect:', error);
          if (error instanceof Error) {
            console.error('Table useEffect Error Stack:', error.stack);
          }
        }
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // Initialize Bootstrap dropdowns
  useEffect(() => {
    const initializeDropdowns = () => {
      // Check if Bootstrap is available
      if (typeof window !== 'undefined' && (window as any).bootstrap) {
        const dropdownElementList = document.querySelectorAll('.dropdown-toggle');
        Array.from(dropdownElementList).map(dropdownToggleEl => 
          new (window as any).bootstrap.Dropdown(dropdownToggleEl)
        );
      }
    };

    // Initialize dropdowns after component mounts
    setTimeout(initializeDropdowns, 100);
  }, [users]);

  if (loading) {
    return (
      <Layout>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
      <Layout>
      <div>
        <div className="row">
          <div className="col-12">
            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
              <div>
                <h4 className="mb-1 font-size-24 fw-semibold" style={{ color: '#495057' }}>Users</h4>
                <p className="text-muted mb-0">Manage all platform users</p>
              </div>
              <div className="page-title-right">
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item">
                    <a href="/dashboard" style={{ color: '#006175' }}>Nyvro</a>
                  </li>
                  <li className="breadcrumb-item active">Users</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="row mb-4">
          <div className="col-xl-3 col-md-6 mb-3">
            <div className="card stats-card h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <p className="text-muted mb-1 fw-medium">Total Users</p>
                    <h3 className="mb-0 fw-bold" style={{ color: '#006175' }}>{stats.total.toLocaleString()}</h3>
                    <small className="text-success">
                      <i className="bx bx-trending-up me-1"></i>
                      All time
                    </small>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-lg rounded-circle d-flex align-items-center justify-content-center" 
                         style={{ backgroundColor: '#00617520' }}>
                      <i className="bx bx-user font-size-24" style={{ color: '#006175' }}></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6 mb-3">
            <div className="card stats-card h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <p className="text-muted mb-1 fw-medium">Active Users</p>
                    <h3 className="mb-0 fw-bold" style={{ color: '#28a745' }}>{stats.active.toLocaleString()}</h3>
                    <small className="text-success">
                      <i className="bx bx-user-check me-1"></i>
                      Currently active
                    </small>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-lg rounded-circle d-flex align-items-center justify-content-center" 
                         style={{ backgroundColor: '#28a74520' }}>
                      <i className="bx bx-user-check font-size-24" style={{ color: '#28a745' }}></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6 mb-3">
            <div className="card stats-card h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <p className="text-muted mb-1 fw-medium">Verified Users</p>
                    <h3 className="mb-0 fw-bold" style={{ color: '#17a2b8' }}>{stats.verified.toLocaleString()}</h3>
                    <small className="text-info">
                      <i className="bx bx-check-circle me-1"></i>
                      Email verified
                    </small>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-lg rounded-circle d-flex align-items-center justify-content-center" 
                         style={{ backgroundColor: '#17a2b820' }}>
                      <i className="bx bx-check-circle font-size-24" style={{ color: '#17a2b8' }}></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6 mb-3">
            <div className="card stats-card h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <p className="text-muted mb-1 fw-medium">Banned Users</p>
                    <h3 className="mb-0 fw-bold" style={{ color: '#dc3545' }}>{stats.banned.toLocaleString()}</h3>
                    <small className="text-danger">
                      <i className="bx bx-user-x me-1"></i>
                      Currently banned
                    </small>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-lg rounded-circle d-flex align-items-center justify-content-center" 
                         style={{ backgroundColor: '#dc354520' }}>
                      <i className="bx bx-user-x font-size-24" style={{ color: '#dc3545' }}></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                {/* Title and Search in same row */}
                <div className="row mb-4">
                  <div className="col-6">
                    <h4 className="card-title mb-1">Users List</h4>
                    <p className="card-title-desc mb-0">
                      Manage all platform users with search, filtering, and actions.
                    </p>
                  </div>
                  <div className="col-6">
                    <div className="d-flex gap-2 justify-content-end">
                      {selectedUserIds.length > 0 && (
                        <>
                          <button
                            className="btn btn-primary"
                            onClick={handleSendEmailToSelected}
                          >
                            <i className="bx bx-envelope me-2"></i>
                            Send Email ({selectedUserIds.length})
                          </button>
                          <button
                            className="btn btn-success"
                            onClick={handleSendPushToSelected}
                          >
                            <i className="bx bx-bell me-2"></i>
                            Send Push ({selectedUserIds.length})
                          </button>
                        </>
                      )}
                      <div className="search-box flex-grow-1" style={{ maxWidth: '300px' }}>
                        <div className="position-relative">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search users..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                          />
                          <i className="bx bx-search-alt search-icon"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Filters Section */}
                <div className="row mb-3">
                  <div className="col-12">
                    <div className="card border">
                      <div className="card-body py-2">
                        <div className="row align-items-center">
                          <div className="col">
                            <div className="d-flex flex-wrap gap-2">
                              {/* Verification Filter */}
                              <div className="filter-item">
                                <select 
                                  className="form-select form-select-sm"
                                  value={filters.verification}
                                  onChange={(e) => handleFilterChange('verification', e.target.value)}
                                  style={{ minWidth: '120px' }}
                                >
                                  <option value="all">All Users</option>
                                  <option value="verified">Verified</option>
                                  <option value="not_verified">Not Verified</option>
                                </select>
                              </div>

                              {/* Restriction Filter */}
                              <div className="filter-item">
                                <select 
                                  className="form-select form-select-sm"
                                  value={filters.restriction}
                                  onChange={(e) => handleFilterChange('restriction', e.target.value)}
                                  style={{ minWidth: '120px' }}
                                >
                                  <option value="all">All Status</option>
                                  <option value="banned">Banned</option>
                                  <option value="not_banned">Not Banned</option>
                                </select>
                              </div>

                          {/* Deleted Filter */}
                          <div className="filter-item">
                            <select
                              className="form-select form-select-sm"
                              value={filters.deleted}
                              onChange={(e) => handleFilterChange('deleted', e.target.value)}
                              style={{ minWidth: '140px' }}
                            >
                              <option value="active">Active Only</option>
                              <option value="deleted">Deleted Only</option>
                              <option value="all">Include Deleted</option>
                            </select>
                          </div>

                              {/* Age Filter */}
                              <div className="filter-item">
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  placeholder="Max Age"
                                  value={filters.maxAge}
                                  onChange={(e) => handleFilterChange('maxAge', e.target.value)}
                                  min="1"
                                  style={{ width: '100px' }}
                                />
                              </div>

                              {/* Online Filter */}
                              <div className="filter-item">
                                <select 
                                  className="form-select form-select-sm"
                                  value={filters.online}
                                  onChange={(e) => handleFilterChange('online', e.target.value)}
                                  style={{ minWidth: '100px' }}
                                >
                                  <option value="all">All</option>
                                  <option value="yes">Online</option>
                                  <option value="no">Offline</option>
                                </select>
                              </div>

                              {/* Relationship Status Filter */}
                              <div className="filter-item">
                                <select 
                                  className="form-select form-select-sm"
                                  value={filters.relationshipStatus.length > 0 ? filters.relationshipStatus[0] : ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === '') {
                                      handleFilterChange('relationshipStatus', []);
                                    } else {
                                      handleFilterChange('relationshipStatus', [value]);
                                    }
                                  }}
                                  style={{ minWidth: '150px' }}
                                >
                                  <option value="">All Status</option>
                                  {filterOptions.relationshipStatuses.map((status: any) => (
                                    <option key={status.id} value={status.id}>
                                      {status.name}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              {/* Positions Filter */}
                              <div className="filter-item">
                                <select 
                                  className="form-select form-select-sm"
                                  value={filters.positions.length > 0 ? filters.positions[0] : ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === '') {
                                      handleFilterChange('positions', []);
                                    } else {
                                      handleFilterChange('positions', [value]);
                                    }
                                  }}
                                  style={{ minWidth: '120px' }}
                                >
                                  <option value="">All Positions</option>
                                  {filterOptions.positions.map((position: any) => (
                                    <option key={position.id} value={position.id}>
                                      {position.name}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              {/* Body Types Filter */}
                              <div className="filter-item">
                                <select 
                                  className="form-select form-select-sm"
                                  value={filters.bodyTypes.length > 0 ? filters.bodyTypes[0] : ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === '') {
                                      handleFilterChange('bodyTypes', []);
                                    } else {
                                      handleFilterChange('bodyTypes', [value]);
                                    }
                                  }}
                                  style={{ minWidth: '120px' }}
                                >
                                  <option value="">All Body Types</option>
                                  {filterOptions.bodyTypes.map((bodyType: any) => (
                                    <option key={bodyType.id} value={bodyType.id}>
                                      {bodyType.name}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              {/* Tags Filter */}
                              <div className="filter-item">
                                <select 
                                  className="form-select form-select-sm"
                                  value={filters.tags.length > 0 ? filters.tags[0] : ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === '') {
                                      handleFilterChange('tags', []);
                                    } else {
                                      handleFilterChange('tags', [value]);
                                    }
                                  }}
                                  style={{ minWidth: '120px' }}
                                >
                                  <option value="">All Tags</option>
                                  {filterOptions.tags.map((tag: any) => (
                                    <option key={tag.id} value={tag.id}>
                                      {tag.name}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              <div className="filter-item">
                                <select
                                  className="form-select form-select-sm"
                                  value={filters.hasAlbum}
                                  onChange={(e) => handleFilterChange('hasAlbum', e.target.value)}
                                  style={{ minWidth: '130px' }}
                                >
                                  <option value="all">All albums</option>
                                  <option value="yes">Has album</option>
                                  <option value="no">No album</option>
                                </select>
                              </div>

                              <div className="filter-item">
                                <select
                                  className="form-select form-select-sm"
                                  value={filters.hasPicture}
                                  onChange={(e) => handleFilterChange('hasPicture', e.target.value)}
                                  style={{ minWidth: '140px' }}
                                >
                                  <option value="all">All gallery</option>
                                  <option value="yes">Has picture</option>
                                  <option value="no">No picture</option>
                                </select>
                              </div>

                              <div className="filter-item">
                                <select
                                  className="form-select form-select-sm"
                                  value={filters.hasProfilePicture}
                                  onChange={(e) => handleFilterChange('hasProfilePicture', e.target.value)}
                                  style={{ minWidth: '160px' }}
                                >
                                  <option value="all">All profile pics</option>
                                  <option value="yes">Has profile picture</option>
                                  <option value="no">No profile picture</option>
                                </select>
                              </div>
                            </div>
                          </div>
                          <div className="col-auto">
                            <button 
                              className="btn btn-sm btn-outline-secondary"
                              onClick={clearAllFilters}
                            >
                              <i className="bx bx-x me-1"></i>
                              Clear All
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  {/* Loading Spinner */}
                  {tableLoading && (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <div className="mt-2">
                        <small className="text-muted">Loading users...</small>
                      </div>
                    </div>
                  )}
                  
                  <table 
                    ref={tableRef}
                    className="table table-striped mb-0"
                    style={{ opacity: tableLoading ? 0.5 : 1, transition: 'opacity 0.3s ease' }}
                  >
                    <thead className="table-light">
                      <tr>
                        <th className="text-center" style={{ width: '50px' }}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={selectedUserIds.length === users.length && users.length > 0}
                            onChange={handleSelectAll}
                          />
                        </th>
                        <th 
                          className="text-center sortable" 
                          onClick={() => handleSort('id')}
                          style={{ cursor: 'pointer' }}
                        >
                          Sr No.
                          {sortInfo.column === 'id' && (
                            <i className={`bx ${sortInfo.direction === 'asc' ? 'bx-sort-up' : 'bx-sort-down'} ms-1`}></i>
                          )}
                        </th>
                        <th 
                          className="sortable" 
                          onClick={() => handleSort('name')}
                          style={{ cursor: 'pointer' }}
                        >
                          Name
                          {sortInfo.column === 'name' && (
                            <i className={`bx ${sortInfo.direction === 'asc' ? 'bx-sort-up' : 'bx-sort-down'} ms-1`}></i>
                          )}
                        </th>
                        <th className="text-center">Media & Activity</th>
                        <th 
                          className="text-center sortable" 
                          onClick={() => handleSort('dob')}
                          style={{ cursor: 'pointer' }}
                        >
                          Age
                          {sortInfo.column === 'dob' && (
                            <i className={`bx ${sortInfo.direction === 'asc' ? 'bx-sort-up' : 'bx-sort-down'} ms-1`}></i>
                          )}
                        </th>
                        <th className="text-center">Status</th>
                        <th className="text-center">OTP</th>
                        <th 
                          className="text-center sortable" 
                          onClick={() => handleSort('created_at')}
                          style={{ cursor: 'pointer' }}
                        >
                          Joined
                          {sortInfo.column === 'created_at' && (
                            <i className={`bx ${sortInfo.direction === 'asc' ? 'bx-sort-up' : 'bx-sort-down'} ms-1`}></i>
                          )}
                        </th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.length > 0 ? (
                        users.map((user) => {
                          const rowClasses = [
                            selectedUserIds.includes(user.id) ? 'table-active' : '',
                            user.is_deleted ? 'table-danger bg-opacity-25' : ''
                          ].filter(Boolean).join(' ');
                          const otpExpiresAt = user.otp_expired_at ? new Date(user.otp_expired_at) : null;
                          const isOtpExpired = otpExpiresAt ? otpExpiresAt.getTime() < Date.now() : false;

                          return (
                          <tr key={user.id} className={rowClasses}>
                            <td className="text-center">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={selectedUserIds.includes(user.id)}
                                onChange={() => handleSelectUser(user.id)}
                              />
                            </td>
                            <td className="text-center">
                              <span className="text-muted">
                                {user.id}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                  {user.profile_picture ? (
                                    <img 
                                      src={user.profile_picture} 
                                      className="rounded-circle me-2" 
                                      width="28" 
                                      height="28" 
                                      alt="User Avatar"
                                      onError={(e) => {
                                        // Hide the broken image and show the dummy icon
                                        e.currentTarget.style.display = 'none';
                                        const parentDiv = e.currentTarget.parentElement;
                                        if (parentDiv) {
                                          const dummyIcon = document.createElement('div');
                                          dummyIcon.className = 'avatar-sm bg-primary rounded-circle d-flex align-items-center justify-content-center me-2';
                                          dummyIcon.style.width = '28px';
                                          dummyIcon.style.height = '28px';
                                          dummyIcon.innerHTML = '<i class="bx bx-user text-white" style="font-size: 14px;"></i>';
                                          parentDiv.appendChild(dummyIcon);
                                        }
                                      }}
                                    />
                                  ) : (
                                    <div className="avatar-sm bg-primary rounded-circle d-flex align-items-center justify-content-center me-2" 
                                         style={{ width: '28px', height: '28px' }}>
                                      <i className="bx bx-user text-white" style={{ fontSize: '14px' }}></i>
                                    </div>
                                  )}
                                  {user.is_live && (
                                    <span 
                                      className="live-indicator" 
                                      title="User is Live"
                                      style={{
                                        position: 'absolute',
                                        top: '0',
                                        right: '8px',
                                        width: '10px',
                                        height: '10px',
                                        background: '#28a745',
                                        border: '2px solid white',
                                        borderRadius: '50%',
                                        animation: 'pulse 2s infinite'
                                      }}
                                    ></span>
                                  )}
                                </div>
                                <div className="flex-grow-1">
                                  <div className="d-flex align-items-center justify-content-between">
                                    <h6 className="mb-0 text-truncate" style={{ maxWidth: '120px' }} title={user.name || '-'}>
                                      {user.name || '-'}
                                    </h6>
                                    <span className="badge bg-light text-dark border ms-2" style={{ fontSize: '10px' }}>
                                      {user.account_number || '-'}
                                    </span>
                                  </div>
                                  <small className="text-muted text-truncate d-block" style={{ maxWidth: '150px' }} title={user.email || '-'}>
                                    {user.email || '-'}
                                  </small>
                                </div>
                              </div>
                            </td>
                            <td className="text-center">
                              <div className="d-flex align-items-center justify-content-center gap-2">
                                {/* Gallery */}
                                <button 
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => openGalleryModal(user.id)}
                                  title="View Gallery"
                                  disabled={!user.gallery_count || user.gallery_count === 0}
                                >
                                  <i className="bx bx-images me-1"></i>
                                  {user.gallery_count || 0}
                                </button>
                                
                                {/* Libraries */}
                                <button 
                                  className="btn btn-sm btn-outline-info"
                                  onClick={() => openLibraryModal(user.id)}
                                  title="View Libraries"
                                  disabled={!user.library_count || user.library_count === 0}
                                >
                                  <i className="bx bx-library me-1"></i>
                                  {user.library_count || 0}
                                </button>
                                
                                {/* Conversations */}
                                <button 
                                  className="btn btn-sm btn-outline-success"
                                  onClick={() => openConversationModal(user.id)}
                                  title="View Conversations"
                                  disabled={!user.conversation_count || user.conversation_count === 0}
                                >
                                  <i className="bx bx-message-dots me-1"></i>
                                  {user.conversation_count || 0}
                                </button>
                                
                                {/* Reports */}
                                <button 
                                  className="btn btn-sm btn-outline-warning"
                                  onClick={() => openReportsModal(user.id)}
                                  title="View Reports"
                                  disabled={!user.reports_count || user.reports_count === 0}
                                >
                                  <i className="bx bx-flag me-1"></i>
                                  {user.reports_count || 0}
                                </button>
                              </div>
                            </td>
                            <td className="text-center">{user.age || '-'}</td>
                            <td className="text-center">
                              <div className="d-flex flex-column gap-1 align-items-center">
                                {user.is_verified && !user.is_banned ? (
                                  <span className="badge bg-success">ACTIVE</span>
                                ) : user.is_verified && user.is_banned ? (
                                  <span className="badge bg-danger">BANNED</span>
                                ) : (
                                  <span className="badge bg-warning text-dark">NOT VERIFIED</span>
                                )}
                                {user.is_deleted && (
                                  <span className="badge bg-dark">Deleted</span>
                                )}
                              </div>
                            </td>
                            <td className="text-center">
                              {user.otp ? (
                                <div className="d-flex flex-column align-items-center gap-1">
                                  <span className="fw-semibold">{user.otp}</span>
                                  {otpExpiresAt && (
                                    <small className={isOtpExpired ? 'text-danger' : 'text-muted'}>
                                      Expires: {otpExpiresAt.toLocaleString()}
                                    </small>
                                  )}
                                  <small className="text-muted">
                                    Attempts: {typeof user.otp_attempts === 'number' ? user.otp_attempts : 0}
                                  </small>
                                </div>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td className="text-center">{user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}</td>
                            <td className="text-center">
                              <div className="dropdown">
                                <button 
                                  className="btn btn-sm btn-outline-secondary dropdown-toggle" 
                                  type="button" 
                                  data-bs-toggle="dropdown" 
                                  aria-expanded="false"
                                >
                                  <i className="bx bx-dots-vertical-rounded"></i>
                                </button>
                                <ul className="dropdown-menu">
                                  <li>
                                    <button 
                                      className="dropdown-item" 
                                      onClick={() => viewUser(user.id)}
                                    >
                                      <i className="bx bx-show me-2"></i>
                                      View User
                                    </button>
                                  </li>
                                  <li>
                                    <button 
                                      className="dropdown-item" 
                                      onClick={() => banUser(user.id)}
                                    >
                                      <i className="bx bx-user-x me-2"></i>
                                      {user.is_banned ? 'Remove Ban' : 'Ban User'}
                                    </button>
                                  </li>
                                  <li>
                                    <button 
                                      className="dropdown-item" 
                                      onClick={() => handleSendEmailToUser(user.id)}
                                    >
                                      <i className="bx bx-envelope me-2"></i>
                                      Send Email
                                    </button>
                                  </li>
                                  <li>
                                    <button 
                                      className="dropdown-item" 
                                      onClick={() => handleSendPushToUser(user.id)}
                                    >
                                      <i className="bx bx-bell me-2"></i>
                                      Send Push
                                    </button>
                                  </li>
                                  <li>
                                    <button 
                                      className="dropdown-item" 
                                      onClick={() => {
                                        setSelectedUser(user);
                                        setShowResetPasswordModal(true);
                                        setNewPassword('');
                                        setConfirmPassword('');
                                      }}
                                    >
                                      <i className="bx bx-lock-alt me-2"></i>
                                      Reset Password
                                    </button>
                                  </li>
                                  <li>
                                    <button 
                                      className="dropdown-item" 
                                      onClick={() => {
                                        setSelectedUser(user);
                                        setShowSetLocationModal(true);
                                        setLatitude('');
                                        setLongitude('');
                                        setLocationName('');
                                      }}
                                    >
                                      <i className="bx bx-map me-2"></i>
                                      Set Location
                                    </button>
                                  </li>
                                  <li><hr className="dropdown-divider" /></li>
                                  <li>
                                    <button 
                                      className="dropdown-item text-danger" 
                                      onClick={() => handleDeleteUser(user.id)}
                                      disabled={deleteLoading}
                                    >
                                      {deleteLoading ? (
                                        <>
                                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                          Deleting...
                                        </>
                                      ) : (
                                        <>
                                          <i className="bx bx-trash me-2"></i>
                                          Delete User
                                        </>
                                      )}
                                    </button>
                                  </li>
                                </ul>
                              </div>
                            </td>
                          </tr>);
                        })
                      ) : (
                        <tr>
                          <td colSpan={9} className="text-center py-4">
                            <div className="d-flex flex-column align-items-center">
                              <i className="bx bx-user-x font-size-48 text-muted mb-2"></i>
                              <p className="text-muted mb-2">
                                {searchTerm || hasActiveFilterValues(filters) 
                                  ? 'No users match your current search or filter criteria.' 
                                  : 'No users found'
                                }
                              </p>
                              {(searchTerm || hasActiveFilterValues(filters)) && (
                                <button 
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={clearAllFilters}
                                >
                                  <i className="bx bx-x me-1"></i>
                                  Clear Filters
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Records per page and Pagination */}
                {users.length > 0 && (
                  <div className="row mt-3">
                    <div className="col-sm-12 col-md-6">
                      <div className="d-flex align-items-center">
                        <span className="me-2">Show</span>
                        <select 
                          className="form-select form-select-sm me-2" 
                          style={{ width: 'auto' }}
                          value={recordsPerPage}
                          onChange={(e) => handleRecordsPerPageChange(parseInt(e.target.value))}
                        >
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                        <span>entries</span>
                      </div>
                    </div>
                    <div className="col-sm-12 col-md-6">
                      <div className="dataTables_info text-end">
                        Showing {pagination.startRecord} to {pagination.endRecord} of {pagination.totalRecords} entries
                      </div>
                    </div>
                  </div>
                )}

                {/* Pagination */}
                {pagination.totalPages > 1 && users.length > 0 && (
                  <div className="row mt-2">
                    <div className="col-sm-12 col-md-12">
                      <div className="dataTables_paginate paging_simple_numbers">
                        <ul className="pagination pagination-rounded justify-content-end mb-0">
                          <li className={`page-item ${pagination.currentPage === 1 ? 'disabled' : ''}`}>
                            <button 
                              className="page-link" 
                              onClick={handlePreviousPage}
                              disabled={pagination.currentPage === 1}
                            >
                              <i className="mdi mdi-chevron-left"></i>
                            </button>
                          </li>
                          
                          {/* Page Numbers */}
                          {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                            let pageNum: number;
                            if (pagination.totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (pagination.currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (pagination.currentPage >= pagination.totalPages - 2) {
                              pageNum = pagination.totalPages - 4 + i;
                            } else {
                              pageNum = pagination.currentPage - 2 + i;
                            }
                            
                            return (
                              <li key={pageNum} className={`page-item ${pagination.currentPage === pageNum ? 'active' : ''}`}>
                                <button 
                                  className="page-link" 
                                  onClick={() => handlePageChange(pageNum)}
                                >
                                  {pageNum}
                                </button>
                              </li>
                            );
                          })}
                          
                          <li className={`page-item ${pagination.currentPage === pagination.totalPages ? 'disabled' : ''}`}>
                            <button 
                              className="page-link" 
                              onClick={handleNextPage}
                              disabled={pagination.currentPage === pagination.totalPages}
                            >
                              <i className="mdi mdi-chevron-right"></i>
                            </button>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Ban User Modal */}
        {showBanModal && selectedUser && (
          <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
            <div className="modal-dialog">
              <div className="modal-content" style={{ position: 'relative' }}>
                {/* Loading Overlay */}
                {banLoading && (
                  <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" 
                       style={{ 
                         backgroundColor: 'rgba(255, 255, 255, 0.8)', 
                         zIndex: 1050,
                         borderRadius: '0.375rem'
                       }}>
                    <div className="text-center">
                      <div className="spinner-border text-primary mb-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <div className="text-muted">
                        {selectedUser.is_banned ? 'Removing ban...' : 'Banning user...'}
                      </div>
                    </div>
                  </div>
                )}
                <div className="modal-header">
                  <h5 className="modal-title">{selectedUser.is_banned ? 'Remove Ban' : 'Ban User'}</h5>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setShowBanModal(false)}
                    disabled={banLoading}
                  ></button>
                </div>
                <div className="modal-body">
                  {selectedUser.is_banned ? (
                    <p>Are you sure you want to remove the ban from <strong>{selectedUser.name}</strong>?</p>
                  ) : (
                    <>
                      <p>Are you sure you want to ban <strong>{selectedUser.name}</strong>?</p>
                      
                      <div className="mb-3">
                        <label className="form-label">Ban Duration (Hours)</label>
                        <div className="input-group">
                          <input
                            type="number"
                            className="form-control"
                            value={banDuration}
                            onChange={(e) => setBanDuration(e.target.value)}
                            placeholder="Enter hours (e.g., 24 for 1 day)"
                            min="1"
                          />
                          <span className="input-group-text">hours</span>
                        </div>
                        <div className="form-text">
                          Enter number of hours or leave empty for permanent ban
                        </div>
                      </div>
                      
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowBanModal(false)}
                    disabled={banLoading}
                  >
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    className={selectedUser.is_banned ? "btn btn-success" : "btn btn-warning"} 
                    onClick={handleBanUser}
                    disabled={banLoading}
                  >
                    {banLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {selectedUser.is_banned ? 'Removing Ban...' : 'Banning User...'}
                      </>
                    ) : (
                      selectedUser.is_banned ? 'Remove Ban' : 'Ban User'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {(showBanModal || showLightbox) && (
          <div className="modal-backdrop fade show"></div>
        )}

        {/* Gallery Modal */}
        {showGalleryModal && selectedUser && (
          <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Gallery - {selectedUser.name}</h5>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => {
                      setShowGalleryModal(false);
                      setGalleryData([]);
                    }}
                  ></button>
                </div>
                <div className="modal-body p-3">
                  {galleryLoading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-3 text-muted">Loading gallery...</p>
                    </div>
                  ) : galleryData.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="bx bx-images font-size-48 text-muted mb-3"></i>
                      <h5 className="text-muted">No Gallery Items</h5>
                      <p className="text-muted">This user has no gallery media.</p>
                    </div>
                  ) : (
                    <div className="row g-3">
                      {galleryData.map((item) => (
                        <div key={item.id} className="col-lg-3 col-md-4 col-sm-6">
                          <div className="card position-relative" style={{ cursor: 'pointer' }} onClick={() => openLightbox(item)}>
                            <div className="position-relative">
                              {item.media_type === 'video' ? (
                                <video 
                                  className="card-img-top" 
                                  style={{ height: '200px', objectFit: 'cover' }}
                                  poster={item.thumbnail_url}
                                >
                                  <source src={item.media_url} type="video/mp4" />
                                </video>
                              ) : (
                                <img 
                                  src={item.media_url} 
                                  className="card-img-top" 
                                  alt="Gallery item"
                                  style={{ height: '200px', objectFit: 'cover' }}
                                  onError={(e) => {
                                    e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkJyb2tlbiBJbWFnZTwvdGV4dD48L3N2Zz4=';
                                  }}
                                />
                              )}
                              
                              {/* Media type icon */}
                              <div className="position-absolute top-0 end-0 m-2">
                                <span className={`badge ${item.media_type === 'video' ? 'bg-danger' : 'bg-primary'} rounded-pill`}>
                                  <i className={`bx ${item.media_type === 'video' ? 'bx-video' : 'bx-image'}`}></i>
                                </span>
                              </div>
                              
                              {/* Deleted badge */}
                              {item.is_deleted && (
                                <div className="position-absolute top-0 start-0 m-2">
                                  <span className="badge bg-warning rounded-pill">Deleted</span>
                                </div>
                              )}
                              
                              {/* Active badge */}
                              {!item.is_deleted && (
                                <div className="position-absolute bottom-0 end-0 m-2">
                                  <span className="badge bg-success rounded-pill">Active</span>
                                </div>
                              )}
                            </div>
                            <div className="card-body p-2">
                              <div className="d-flex justify-content-between align-items-center">
                                <small className="text-muted">
                                  {new Date(item.created_at).toLocaleDateString()}
                                </small>
                                <small className="text-muted">
                                  {item.media_type === 'video' ? 'Video' : 'Image'}
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => {
                      setShowGalleryModal(false);
                      setGalleryData([]);
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Lightbox Modal */}
        {showLightbox && selectedMedia && (
          <div className="modal fade show" style={{ display: 'block', zIndex: 1060 }} tabIndex={-1}>
            <div className="modal-dialog modal-fullscreen">
              <div className="modal-content bg-dark">
                <div className="modal-header border-0">
                  <div className="d-flex align-items-center">
                    <span className={`badge ${selectedMedia.media_type === 'video' ? 'bg-danger' : 'bg-primary'} me-2`}>
                      <i className={`bx ${selectedMedia.media_type === 'video' ? 'bx-video' : 'bx-image'}`}></i>
                    </span>
                    <span className="text-white">
                      {selectedMedia.media_type === 'video' ? 'Video' : 'Image'} - {new Date(selectedMedia.created_at).toLocaleDateString()}
                    </span>
                    {selectedMedia.is_deleted && (
                      <span className="badge bg-warning ms-2">Deleted</span>
                    )}
                  </div>
                  <button 
                    type="button" 
                    className="btn-close btn-close-white" 
                    onClick={closeLightbox}
                  ></button>
                </div>
                <div className="modal-body d-flex align-items-center justify-content-center p-0">
                  <div className="text-center">
                    {selectedMedia.media_type === 'video' ? (
                      <video 
                        controls 
                        autoPlay 
                        className="img-fluid"
                        style={{ maxHeight: '80vh', maxWidth: '100%' }}
                      >
                        <source src={selectedMedia.media_url} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    ) : (
                      <img 
                        src={selectedMedia.media_url} 
                        alt="Gallery item"
                        className="img-fluid"
                        style={{ maxHeight: '80vh', maxWidth: '100%', objectFit: 'contain' }}
                        onError={(e) => {
                          e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjMzMzIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIyMCIgZmlsbD0iI2ZmZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkJyb2tlbiBJbWFnZTwvdGV4dD48L3N2Zz4=';
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Library Modal */}
        {showLibraryModal && selectedUser && (
          <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Libraries - {selectedUser.name}</h5>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => {
                      setShowLibraryModal(false);
                      setLibraryData([]);
                    }}
                  ></button>
                </div>
                <div className="modal-body p-3">
                  {libraryLoading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-3 text-muted">Loading libraries...</p>
                    </div>
                  ) : libraryData.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="bx bx-library font-size-48 text-muted mb-3"></i>
                      <h5 className="text-muted">No Libraries</h5>
                      <p className="text-muted">This user has no library albums.</p>
                    </div>
                  ) : (
                    <div className="row g-4">
                      {libraryData.map((library) => (
                        <div key={library.album_id} className="col-12">
                          <div className="card">
                            <div className="card-header d-flex justify-content-between align-items-center">
                              <div className="d-flex align-items-center">
                                <h6 className="mb-0 me-3">{library.album_name}</h6>
                                {library.is_deleted && (
                                  <span className="badge bg-warning">Deleted Album</span>
                                )}
                                {!library.is_deleted && (
                                  <span className="badge bg-success">Active Album</span>
                                )}
                              </div>
                              <div className="text-muted small">
                                {library.media_count} items ({library.active_media_count} active, {library.deleted_media_count} deleted)
                              </div>
                            </div>
                            <div className="card-body">
                              {library.media.length === 0 ? (
                                <div className="text-center py-3">
                                  <i className="bx bx-image text-muted mb-2" style={{ fontSize: '24px' }}></i>
                                  <p className="text-muted mb-0">No media in this album</p>
                                </div>
                              ) : (
                                <div className="row g-2">
                                  {library.media.map((mediaItem: any) => (
                                    <div key={mediaItem.id} className="col-lg-2 col-md-3 col-sm-4 col-6">
                                      <div className="card position-relative" style={{ cursor: 'pointer' }} onClick={() => openLightbox(mediaItem)}>
                                        <div className="position-relative">
                                          {mediaItem.media_type === 'video' ? (
                                            <video 
                                              className="card-img-top" 
                                              style={{ height: '120px', objectFit: 'cover' }}
                                              poster={mediaItem.thumbnail_url}
                                            >
                                              <source src={mediaItem.media_url} type="video/mp4" />
                                            </video>
                                          ) : (
                                            <img 
                                              src={mediaItem.media_url} 
                                              className="card-img-top" 
                                              alt="Library media"
                                              style={{ height: '120px', objectFit: 'cover' }}
                                              onError={(e) => {
                                                e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkJyb2tlbiBJbWFnZTwvdGV4dD48L3N2Zz4=';
                                              }}
                                            />
                                          )}
                                          
                                          {/* Media type icon */}
                                          <div className="position-absolute top-0 end-0 m-1">
                                            <span className={`badge ${mediaItem.media_type === 'video' ? 'bg-danger' : 'bg-primary'} rounded-pill`} style={{ fontSize: '10px' }}>
                                              <i className={`bx ${mediaItem.media_type === 'video' ? 'bx-video' : 'bx-image'}`}></i>
                                            </span>
                                          </div>
                                          
                                          {/* Deleted badge */}
                                          {mediaItem.is_deleted && (
                                            <div className="position-absolute top-0 start-0 m-1">
                                              <span className="badge bg-warning rounded-pill" style={{ fontSize: '10px' }}>Deleted</span>
                                            </div>
                                          )}
                                          
                                          {/* Active badge */}
                                          {!mediaItem.is_deleted && (
                                            <div className="position-absolute bottom-0 end-0 m-1">
                                              <span className="badge bg-success rounded-pill" style={{ fontSize: '10px' }}>Active</span>
                                            </div>
                                          )}
                                        </div>
                                        <div className="card-body p-1">
                                          <small className="text-muted">
                                            {new Date(mediaItem.created_at).toLocaleDateString()}
                                          </small>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => {
                      setShowLibraryModal(false);
                      setLibraryData([]);
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}


        {/* Reports Modal */}
        {showReportsModal && selectedUser && (
          <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header py-2">
                  <h6 className="modal-title mb-0">Reports - {selectedUser.name}</h6>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => {
                      setShowReportsModal(false);
                      setUserReports([]);
                    }}
                  ></button>
                </div>
                <div className="modal-body p-3">
                  {reportsLoading ? (
                    <div className="text-center py-3">
                      <div className="spinner-border spinner-border-sm text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2 text-muted small">Loading reports...</p>
                    </div>
                  ) : userReports.length === 0 ? (
                    <div className="text-center py-3">
                      <i className="bx bx-check-circle text-success mb-2" style={{ fontSize: '28px' }}></i>
                      <div className="text-success small">No Reports</div>
                      <div className="text-muted small">This user has no reports.</div>
                    </div>
                  ) : (
                    <div className="list-group">
                      {userReports.map((report) => (
                        <div key={report.id} className="list-group-item list-group-item-action d-flex align-items-start p-2">
                          <div className="me-2">
                            {report.reporter.profile_picture ? (
                              <img 
                                src={report.reporter.profile_picture} 
                                alt="Reporter" 
                                className="rounded-circle"
                                style={{ width: '36px', height: '36px', objectFit: 'cover' }}
                              />
                            ) : (
                              <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center" 
                                   style={{ width: '36px', height: '36px' }}>
                                <i className="bx bx-user text-white" style={{ fontSize: '14px' }}></i>
                              </div>
                            )}
                          </div>
                          <div className="flex-grow-1">
                            <div className="d-flex justify-content-between align-items-start">
                              <div className="me-2">
                                <div className="fw-semibold small mb-1 text-truncate" style={{ maxWidth: '260px' }}>{report.reporter.name}</div>
                                <div className="text-muted small">Acct #{report.reporter.account_number}</div>
                              </div>
                              <div className="text-end">
                                <span className={`badge rounded-pill ${
                                  report.status === 'pending' ? 'bg-warning' :
                                  report.status === 'reviewed' ? 'bg-info' :
                                  report.status === 'resolved' ? 'bg-success' :
                                  'bg-secondary'
                                }`}>
                                  {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                                </span>
                                <div className="text-muted small mt-1">
                                  {new Date(report.created_at).toLocaleDateString()}
                                </div>
                              </div>
                            </div>
                            <div className="d-flex align-items-center mt-2">
                              <span className="small text-muted me-2">Reason:</span>
                              <span className="badge bg-danger">{report.report_reason.charAt(0).toUpperCase() + report.report_reason.slice(1)}</span>
                            </div>
                            {report.report_description && (
                              <div className="small text-muted mt-2" style={{ lineHeight: 1.3 }}>
                                {report.report_description}
                              </div>
                            )}
                            {report.admin_notes && (
                              <div className="small text-info mt-1" style={{ lineHeight: 1.3 }}>
                                {report.admin_notes}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="modal-footer py-2">
                  <button 
                    type="button" 
                    className="btn btn-sm btn-secondary" 
                    onClick={() => {
                      setShowReportsModal(false);
                      setUserReports([]);
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* User Details Modal */}
        {showUserDetailsModal && selectedUser && (
          <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">User Profile - {selectedUser.name}</h5>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setShowUserDetailsModal(false)}
                  ></button>
                </div>
                <div className="modal-body p-4">
                  <div className="row g-4">
                    {/* Left Column - Profile Card */}
                    <div className="col-xl-4">
                      <div className="card overflow-hidden mb-4">
                        <div className="bg-primary-subtle">
                          <div className="row">
                            <div className="col-7">
                              <div className="text-primary p-3">
                                <h5 className="text-primary mb-1">User Profile</h5>
                                <p className="mb-0">Account #{selectedUser.account_number}</p>
                              </div>
                            </div>
                            <div className="col-5 align-self-end">
                              <div className="text-center p-3">
                                {selectedUser.profile_picture ? (
                                  <img 
                                    src={selectedUser.profile_picture} 
                                    alt="Profile" 
                                    className="img-fluid rounded-circle"
                                    style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                                  />
                                ) : (
                                  <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center mx-auto" 
                                       style={{ width: '80px', height: '80px' }}>
                                    <i className="bx bx-user text-white" style={{ fontSize: '32px' }}></i>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="card-body pt-3">
                          <div className="row">
                            <div className="col-sm-4">
                              <div className="avatar-md profile-user-wid mb-3">
                                {selectedUser.profile_picture ? (
                                  <img 
                                    src={selectedUser.profile_picture} 
                                    alt="" 
                                    className="img-thumbnail rounded-circle"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  />
                                ) : (
                                  <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center" 
                                       style={{ width: '100%', height: '100%' }}>
                                    <i className="bx bx-user text-white" style={{ fontSize: '24px' }}></i>
                                  </div>
                                )}
                              </div>
                              <h5 className="font-size-15 text-truncate mb-1">{selectedUser.name}</h5>
                              <p className="text-muted mb-0 text-truncate">
                                {selectedUser.is_verified ? 'Verified User' : 'Unverified User'}
                              </p>
                            </div>

                            <div className="col-sm-8">
                              <div className="pt-2">
                                <div className="row">
                                  <div className="col-6">
                                    <h5 className="font-size-15 mb-1">{selectedUser.gallery_count || 0}</h5>
                                    <p className="text-muted mb-0">Gallery</p>
                                  </div>
                                  <div className="col-6">
                                    <h5 className="font-size-15 mb-1">{selectedUser.conversation_count || 0}</h5>
                                    <p className="text-muted mb-0">Conversations</p>
                                  </div>
                                </div>
                                <div className="mt-3 d-flex flex-wrap gap-2">
                                  <span className={`badge ${selectedUser.is_banned ? 'bg-danger' : 'bg-success'}`}>
                                    {selectedUser.is_banned ? 'Banned' : 'Active'}
                                  </span>
                                  {selectedUser.is_verified && (
                                    <span className="badge bg-primary">Verified</span>
                                  )}
                                  {selectedUser.is_deleted && (
                                    <span className="badge bg-dark">Deleted</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Personal Information Card */}
                      <div className="card">
                        <div className="card-body">
                          <h4 className="card-title mb-3">Personal Information</h4>
                          <div className="table-responsive">
                            <table className="table table-nowrap mb-0">
                              <tbody>
                                <tr>
                                  <th scope="row" className="py-2">Full Name:</th>
                                  <td className="py-2">{selectedUser.name || '-'}</td>
                                </tr>
                                <tr>
                                  <th scope="row" className="py-2">Account Number:</th>
                                  <td className="py-2">{selectedUser.account_number || '-'}</td>
                                </tr>
                                <tr>
                                  <th scope="row" className="py-2">Email:</th>
                                  <td className="py-2">{selectedUser.email || '-'}</td>
                                </tr>
                                <tr>
                                  <th scope="row" className="py-2">Age:</th>
                                  <td className="py-2">{selectedUser.age || '-'}</td>
                                </tr>
                                <tr>
                                  <th scope="row" className="py-2">Status:</th>
                                  <td className="py-2">
                                    <span className={`badge ${selectedUser.is_banned ? 'bg-danger' : 'bg-success'}`}>
                                      {selectedUser.is_banned ? 'Banned' : 'Active'}
                                    </span>
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row" className="py-2">Verification:</th>
                                  <td className="py-2">
                                    <span className={`badge ${selectedUser.is_verified ? 'bg-primary' : 'bg-secondary'}`}>
                                      {selectedUser.is_verified ? 'Verified' : 'Not Verified'}
                                    </span>
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row" className="py-2">Password:</th>
                                  <td className="py-2">
                                    {selectedUser.password ? (
                                      <code className="small text-wrap d-block" style={{ wordBreak: 'break-all' }}>
                                        {selectedUser.password}
                                      </code>
                                    ) : (
                                      <span className="text-muted">-</span>
                                    )}
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row" className="py-2">OTP:</th>
                                  <td className="py-2">
                                    {selectedUser.otp ? (
                                      <span className="fw-semibold">{selectedUser.otp}</span>
                                    ) : (
                                      <span className="text-muted">No active OTP</span>
                                    )}
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row" className="py-2">OTP Expires:</th>
                                  <td className="py-2">
                                    {selectedUser.otp_expired_at ? (
                                      <span className={new Date(selectedUser.otp_expired_at).getTime() < Date.now() ? 'text-danger' : ''}>
                                        {new Date(selectedUser.otp_expired_at).toLocaleString()}
                                      </span>
                                    ) : (
                                      <span className="text-muted">-</span>
                                    )}
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row" className="py-2">OTP Attempts:</th>
                                  <td className="py-2">
                                    {typeof selectedUser.otp_attempts === 'number' ? selectedUser.otp_attempts : 0}
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row" className="py-2">Joined:</th>
                                  <td className="py-2">{selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleDateString() : '-'}</td>
                                </tr>
                                <tr>
                                  <th scope="row" className="py-2">Deleted:</th>
                                  <td className="py-2">
                                    {selectedUser.is_deleted ? (
                                      <>
                                        <span className="badge bg-dark me-2">Deleted</span>
                                        {selectedUser.deleted_at && (
                                          <small className="text-muted">
                                            {new Date(selectedUser.deleted_at).toLocaleString()}
                                          </small>
                                        )}
                                      </>
                                    ) : (
                                      'No'
                                    )}
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row" className="py-2">Latitude:</th>
                                  <td className="py-2">
                                    {selectedUser.latitude !== null && selectedUser.latitude !== undefined ? (
                                      <code>{selectedUser.latitude}</code>
                                    ) : (
                                      <span className="text-muted">-</span>
                                    )}
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row" className="py-2">Longitude:</th>
                                  <td className="py-2">
                                    {selectedUser.longitude !== null && selectedUser.longitude !== undefined ? (
                                      <code>{selectedUser.longitude}</code>
                                    ) : (
                                      <span className="text-muted">-</span>
                                    )}
                                  </td>
                                </tr>
                                {selectedUser.location_name && (
                                  <tr>
                                    <th scope="row" className="py-2">Location Name:</th>
                                    <td className="py-2">{selectedUser.location_name}</td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right Column - Statistics and Details */}
                    <div className="col-xl-8">
                      {/* Statistics Cards */}
                      <div className="row g-3 mb-4">
                        <div className="col-md-4">
                          <div 
                            className="card mini-stats-wid"
                            onClick={() => {
                              if (selectedUser.gallery_count && selectedUser.gallery_count > 0) {
                                setShowUserDetailsModal(false);
                                setShowGalleryModal(true);
                              }
                            }}
                            style={{
                              cursor: (selectedUser.gallery_count && selectedUser.gallery_count > 0) ? 'pointer' : 'default',
                              opacity: (selectedUser.gallery_count && selectedUser.gallery_count > 0) ? 1 : 0.6,
                              pointerEvents: (selectedUser.gallery_count && selectedUser.gallery_count > 0) ? 'auto' : 'none'
                            }}
                          >
                            <div className="card-body">
                              <div className="d-flex">
                                <div className="flex-grow-1">
                                  <p className="text-muted fw-medium mb-2">Gallery Items</p>
                                  <h4 className="mb-0">{selectedUser.gallery_count || 0}</h4>
                                </div>
                                <div className="flex-shrink-0 align-self-center">
                                  <div className="mini-stat-icon avatar-sm rounded-circle bg-primary">
                                    <span className="avatar-title">
                                      <i className="bx bx-image font-size-24"></i>
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div 
                            className="card mini-stats-wid"
                            onClick={() => {
                              if (selectedUser.library_count && selectedUser.library_count > 0) {
                                setShowUserDetailsModal(false);
                                setShowLibraryModal(true);
                              }
                            }}
                            style={{
                              cursor: (selectedUser.library_count && selectedUser.library_count > 0) ? 'pointer' : 'default',
                              opacity: (selectedUser.library_count && selectedUser.library_count > 0) ? 1 : 0.6,
                              pointerEvents: (selectedUser.library_count && selectedUser.library_count > 0) ? 'auto' : 'none'
                            }}
                          >
                            <div className="card-body">
                              <div className="d-flex">
                                <div className="flex-grow-1">
                                  <p className="text-muted fw-medium mb-2">Libraries</p>
                                  <h4 className="mb-0">{selectedUser.library_count || 0}</h4>
                                </div>
                                <div className="flex-shrink-0 align-self-center">
                                  <div className="avatar-sm mini-stat-icon rounded-circle bg-primary">
                                    <span className="avatar-title">
                                      <i className="bx bx-folder font-size-24"></i>
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div 
                            className="card mini-stats-wid"
                            onClick={() => {
                              if (selectedUser.conversation_count && selectedUser.conversation_count > 0) {
                                setShowUserDetailsModal(false);
                                navigate(`/chat/${selectedUser.id}`);
                              }
                            }}
                            style={{
                              cursor: (selectedUser.conversation_count && selectedUser.conversation_count > 0) ? 'pointer' : 'default',
                              opacity: (selectedUser.conversation_count && selectedUser.conversation_count > 0) ? 1 : 0.6,
                              pointerEvents: (selectedUser.conversation_count && selectedUser.conversation_count > 0) ? 'auto' : 'none'
                            }}
                          >
                            <div className="card-body">
                              <div className="d-flex">
                                <div className="flex-grow-1">
                                  <p className="text-muted fw-medium mb-2">Conversations</p>
                                  <h4 className="mb-0">{selectedUser.conversation_count || 0}</h4>
                                </div>
                                <div className="flex-shrink-0 align-self-center">
                                  <div className="avatar-sm mini-stat-icon rounded-circle bg-primary">
                                    <span className="avatar-title">
                                      <i className="bx bx-message font-size-24"></i>
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* User Activity Card */}
                      <div className="card">
                        <div className="card-body">
                          <h4 className="card-title mb-3">User Activity</h4>
                          <div className="table-responsive">
                            <table className="table table-nowrap table-hover mb-0">
                              <thead>
                                <tr>
                                  <th scope="col" className="py-2">Activity</th>
                                  <th scope="col" className="py-2">Count</th>
                                  <th scope="col" className="py-2 text-center">Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td className="py-2">Gallery Items</td>
                                  <td className="py-2">{selectedUser.gallery_count || 0}</td>
                                  <td className="py-2 text-center">
                                    {(selectedUser.gallery_count && selectedUser.gallery_count > 0) && (
                                      <button 
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => {
                                          setShowUserDetailsModal(false);
                                          setShowGalleryModal(true);
                                        }}
                                      >
                                        <i className="bx bx-image me-1"></i>
                                        View
                                      </button>
                                    )}
                                  </td>
                                </tr>
                                <tr>
                                  <td className="py-2">Libraries</td>
                                  <td className="py-2">{selectedUser.library_count || 0}</td>
                                  <td className="py-2 text-center">
                                    {(selectedUser.library_count && selectedUser.library_count > 0) && (
                                      <button 
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => {
                                          setShowUserDetailsModal(false);
                                          setShowLibraryModal(true);
                                        }}
                                      >
                                        <i className="bx bx-folder me-1"></i>
                                        View
                                      </button>
                                    )}
                                  </td>
                                </tr>
                                <tr>
                                  <td className="py-2">Conversations</td>
                                  <td className="py-2">{selectedUser.conversation_count || 0}</td>
                                  <td className="py-2 text-center">
                                    {(selectedUser.conversation_count && selectedUser.conversation_count > 0) && (
                                      <button 
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => {
                                          setShowUserDetailsModal(false);
                                          navigate(`/chat/${selectedUser.id}`);
                                        }}
                                      >
                                        <i className="bx bx-message me-1"></i>
                                        View
                                      </button>
                                    )}
                                  </td>
                                </tr>
                                <tr>
                                  <td className="py-2">Reports</td>
                                  <td className="py-2">{selectedUser.reports_count || 0}</td>
                                  <td className="py-2 text-center">
                                    {(selectedUser.reports_count && selectedUser.reports_count > 0) && (
                                      <button 
                                        className="btn btn-sm btn-outline-warning"
                                        onClick={() => {
                                          setShowUserDetailsModal(false);
                                          setShowReportsModal(true);
                                        }}
                                      >
                                        <i className="bx bx-flag me-1"></i>
                                        View
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowUserDetailsModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reset Password Modal */}
        {showResetPasswordModal && selectedUser && (
          <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Reset Password - {selectedUser.name}</h5>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => {
                      setShowResetPasswordModal(false);
                      setNewPassword('');
                      setConfirmPassword('');
                      setSelectedUser(null);
                    }}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label htmlFor="newPassword" className="form-label">New Password</label>
                    <input
                      type="password"
                      className="form-control"
                      id="newPassword"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password (min 6 characters)"
                      minLength={6}
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="confirmPassword" className="form-label">Confirm Password</label>
                    <input
                      type="password"
                      className="form-control"
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      minLength={6}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => {
                      setShowResetPasswordModal(false);
                      setNewPassword('');
                      setConfirmPassword('');
                      setSelectedUser(null);
                    }}
                    disabled={resetPasswordLoading}
                  >
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-primary" 
                    onClick={handleResetPassword}
                    disabled={resetPasswordLoading}
                  >
                    {resetPasswordLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Resetting...
                      </>
                    ) : (
                      'Reset Password'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Set Location Modal */}
        {showSetLocationModal && selectedUser && (
          <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Set Location - {selectedUser.name}</h5>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => {
                      setShowSetLocationModal(false);
                      setLatitude('');
                      setLongitude('');
                      setLocationName('');
                      setSelectedUser(null);
                    }}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label htmlFor="latitude" className="form-label">Latitude *</label>
                    <input
                      type="number"
                      className="form-control"
                      id="latitude"
                      value={latitude}
                      onChange={(e) => setLatitude(e.target.value)}
                      placeholder="Enter latitude (-90 to 90)"
                      min={-90}
                      max={90}
                      step="any"
                    />
                    <small className="form-text text-muted">Must be between -90 and 90</small>
                  </div>
                  <div className="mb-3">
                    <label htmlFor="longitude" className="form-label">Longitude *</label>
                    <input
                      type="number"
                      className="form-control"
                      id="longitude"
                      value={longitude}
                      onChange={(e) => setLongitude(e.target.value)}
                      placeholder="Enter longitude (-180 to 180)"
                      min={-180}
                      max={180}
                      step="any"
                    />
                    <small className="form-text text-muted">Must be between -180 and 180</small>
                  </div>
                  <div className="mb-3">
                    <label htmlFor="locationName" className="form-label">Location Name (Optional)</label>
                    <input
                      type="text"
                      className="form-control"
                      id="locationName"
                      value={locationName}
                      onChange={(e) => setLocationName(e.target.value)}
                      placeholder="Enter location name (e.g., New York, NY)"
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => {
                      setShowSetLocationModal(false);
                      setLatitude('');
                      setLongitude('');
                      setLocationName('');
                      setSelectedUser(null);
                    }}
                    disabled={setLocationLoading}
                  >
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-primary" 
                    onClick={handleSetLocation}
                    disabled={setLocationLoading}
                  >
                    {setLocationLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Updating...
                      </>
                    ) : (
                      'Update Location'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal Backdrops */}
        {(showGalleryModal || showLibraryModal || showReportsModal || showUserDetailsModal || showResetPasswordModal || showSetLocationModal) && (
          <div className="modal-backdrop fade show"></div>
        )}
      </div>
    </Layout>
  </>
  );
};

export default Users;